import ButtonLink from "@/Components/ButtonLink";
import React from "react";

// The CMS field for this description is plain text, not rich text: content
// editors separate bullet points with a "-" (either on a new line or inline)
// instead of using a real <ul>/<li>. Split on a hyphen that's followed by
// whitespace + a capital letter, so the em dash inside "Clutch — Summer
// 2026" is left alone, whatever whitespace (space or newline) surrounds the
// separator in the raw CMS value. If the value ever does contain real HTML,
// render it as-is.
const renderDescription = (raw: string) => {
  if (/<[a-z][\s\S]*>/i.test(raw)) {
    return <div dangerouslySetInnerHTML={{ __html: raw }} />;
  }

  const [intro, ...items] = raw
    .split(/\s*-\s*(?=[A-Z])/)
    .map((part) => part.trim())
    .filter(Boolean);

  return (
    <>
      {intro && <p>{intro}</p>}
      {items.length > 0 && (
        <ul>
          {items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      )}
    </>
  );
};

interface SubmenuRightSectionV2Props {
  subTitle?: string;
  title?: string;
  titleSec?: string;
  bgImgDesktop?: string;
  bgImgIpad?: string;
  bgImgMobile?: string;
  description?: string;
  btnLabel?: string;
  btnHref?: string;
  onBtnClick?: (href?: string) => void;
}
const SubmenuRightSectionV2 = ({
  subTitle,
  title,
  titleSec,
  bgImgDesktop,
  bgImgIpad,
  bgImgMobile,
  description,
  btnLabel,
  btnHref,
  onBtnClick,
}: SubmenuRightSectionV2Props) => {
  // On tablets (lg up to xl) the card sits below the links as a full-width
  // banner: copy on the left, CTA on the right. At xl it returns to the tall
  // sidebar column beside the links.
  return (
    <div className="w-full max-w-[100%] xl:max-w-[350px] lg:pb-0 pb-[40px] min-[1280px]:max-[1366px]:!max-w-[320px]">
      <div
        className="card-wrp responsive-bg-image xl:p-[32px] p-[22px] bg-[#000]  h-full flex flex-col lg:flex-row lg:items-center lg:gap-[24px] xl:flex-col xl:items-stretch xl:gap-0  min-h-[246px] md:min-h-[400px] lg:min-h-0 xl:min-h-[500px]"
        style={
          {
            "--bg-desktop": bgImgDesktop,
            "--bg-ipad": bgImgIpad,
            "--bg-mobile": bgImgMobile,
          } as React.CSSProperties
        }
      >
        <div className="flex flex-col flex-1 w-full">
          <div className="title">
            {subTitle && (
              <h6 className="text-[14px] font-[600] text-[var(--color-green-500)] mb-[4px] leading-[100%]">
                {subTitle}
              </h6>
            )}
            {title && (
              <h4 className="xl:text-[48px] md:text-[36px] text-[30px]  font-[var(--font-weight-bold)] text-[var(--color-green-500)] leading-[1.5] lg:mb-0 mb-4">
                {title}
              </h4>
            )}
            {titleSec && (
              <h3 className="my-[12px] xl:text-[20px] text-[18px]  font-[var(--font-weight-bold)] text-white leading-[1.3] !tracking-[0]">
                {titleSec}
              </h3>
            )}
          </div>
          <div className="body flex-1">
            {description && (
              <div className="rich-menu-desc xl:text-[16px] text-[14px] font-[400] text-white leading-[1.4]">
                {renderDescription(description)}
              </div>
            )}
          </div>
        </div>
        {/* The CTA is optional in the CMS - without a label there is nothing
            to click, so the button is dropped rather than rendered empty. */}
        {btnLabel && (
          <div className="footer w-max ml-auto lg:w-auto lg:shrink-0 xl:w-full">
            <ButtonLink
              label={btnLabel}
              href={btnHref || "#!"}
              onClick={() => onBtnClick?.(btnHref)}
              className="mt-[20px] lg:mt-0 xl:mt-[20px] !w-full lg:!w-[260px] xl:!w-full justify-center bg-text-button hover:bg-blue-700 text-primary-text-white md:!text-[16px] !text-[14px] !p-[12px]"
            />
          </div>
        )}
      </div>
      <style jsx>{`
        .responsive-bg-image {
          background-image: var(--bg-desktop);
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
        }
        @media (max-width: 767px) {
          .responsive-bg-image {
            background-image: var(--bg-mobile);
          }
        }
        @media (min-width: 768px) and (max-width: 1023px) {
          .responsive-bg-image {
            background-image: var(--bg-ipad);
            background-position: right;
          }
        }
      `}</style>
    </div>
  );
};

export default SubmenuRightSectionV2;
